import {Menu, type WrapperContentPropsFn} from '@dropbox/dig-components/menu';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CheckmarkCircleFill,
  CheckmarkLine,
  CircleStandardLine,
  FailFill,
} from '@dropbox/dig-icons/assets';
import {StatusButton} from 'components/DSYS/StatusButton';
import {getStatusStyle} from 'components/DSYS/StatusButtonIcon';
import {t} from 'i18next';

import metricStatusStyles from './WorkstreamMetricStatus.module.css';
import statusControlStyles from './WorkstreamStatusControl.module.css';

export const OUTCOME_STATUS_VALUES = ['open', 'completed', 'missed'] as const;

export type OutcomeStatus = (typeof OUTCOME_STATUS_VALUES)[number];

export type OutcomeStatusInput = {
  complete?: boolean | null;
  status?: string | null;
};

export type OutcomeStatusUpdate = {
  complete: boolean;
  status: 'completed' | 'missed' | null;
};

export const isTerminalOutcomeStatus = (status: OutcomeStatus): boolean => status !== 'open';

export const getOutcomeStatus = ({complete, status}: OutcomeStatusInput): OutcomeStatus => {
  if (status === 'open' || status === 'completed' || status === 'missed') {
    return status;
  }

  return complete ? 'completed' : 'open';
};

export const getOutcomeStatusUpdate = (status: OutcomeStatus): OutcomeStatusUpdate => ({
  complete: isTerminalOutcomeStatus(status),
  status: status === 'open' ? null : status,
});

const getStatusLabel = (status: OutcomeStatus): string =>
  status === 'completed' ? t('complete').toString() : t(status).toString();

const getStatusIcon = (status: OutcomeStatus) => {
  switch (status) {
    case 'completed':
      return {color: getStatusStyle('complete').color, src: CheckmarkCircleFill};
    case 'missed':
      return {color: getStatusStyle('missed').color, src: FailFill};
    case 'open':
      return {color: getStatusStyle('not_started').color, src: CircleStandardLine};
  }
};

export const getCompactOutcomeStatusButtonStatus = (status: OutcomeStatus) => {
  switch (status) {
    case 'completed':
      return 'complete';
    case 'missed':
      return 'missed';
    case 'open':
      return 'not_started';
  }
};

const OutcomeStatusTagContent = ({
  status,
  size = 'xsmall',
}: {
  status: OutcomeStatus;
  size?: 'xsmall' | 'small';
}) => {
  const {color, src} = getStatusIcon(status);
  const iconSize = size === 'small' ? 16 : 12;

  return (
    <span className={metricStatusStyles.metricStatusContent}>
      <UIIcon src={src} width={iconSize} height={iconSize} color={color} />
      <span>{getStatusLabel(status)}</span>
    </span>
  );
};

const OutcomeStatusMenuContent = ({
  getContentProps,
  onSelect,
  selectedStatus,
}: {
  getContentProps: WrapperContentPropsFn;
  onSelect: (status: OutcomeStatus) => void;
  selectedStatus: OutcomeStatus;
}) => (
  <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 160}}>
    <Menu.Segment withLabel={t('status').toString()}>
      {OUTCOME_STATUS_VALUES.map((status) => (
        <Menu.ActionItem
          key={status}
          aria-label={getStatusLabel(status)}
          onClick={() => onSelect(status)}
          withRightAccessory={
            selectedStatus === status ? <UIIcon src={CheckmarkLine} /> : undefined
          }
        >
          <span className={metricStatusStyles.metricStatusTag}>
            <OutcomeStatusTagContent status={status} />
          </span>
        </Menu.ActionItem>
      ))}
    </Menu.Segment>
  </Menu.Content>
);

export const OutcomeStatusControl = ({
  complete,
  compact = false,
  mediumIconOnly = false,
  disabled = false,
  onChange,
  size = 'xsmall',
  status,
}: OutcomeStatusInput & {
  compact?: boolean;
  mediumIconOnly?: boolean;
  disabled?: boolean;
  onChange?: (update: OutcomeStatusUpdate) => void;
  size?: 'xsmall' | 'small';
}) => {
  const selectedStatus = getOutcomeStatus({complete, status});
  const label = getStatusLabel(selectedStatus);
  const statusTagClassName = `${metricStatusStyles.metricStatusTag} ${
    size === 'small' ? metricStatusStyles.metricStatusTagSmall : ''
  }`;

  if (!onChange) {
    if (compact || mediumIconOnly) {
      return (
        <StatusButton
          status={getCompactOutcomeStatusButtonStatus(selectedStatus)}
          hideText
          hideIconEnd
          hoverBackgroundOnly
          hoverChromeOnly
          disableHoverStyles
          nonInteractive
          compactIconOnly={compact && !mediumIconOnly}
          mediumIconOnly={mediumIconOnly}
          size={mediumIconOnly ? 'medium' : 'small'}
          buttonProps={{'aria-label': label, tabIndex: -1}}
        />
      );
    }

    return (
      <span className={statusTagClassName}>
        <OutcomeStatusTagContent status={selectedStatus} size={size} />
      </span>
    );
  }

  return (
    <Menu.Wrapper>
      {({getContentProps, getTriggerProps}) => (
        <>
          {compact || mediumIconOnly ? (
            <StatusButton
              status={getCompactOutcomeStatusButtonStatus(selectedStatus)}
              disabled={disabled}
              hideText
              hideIconEnd
              hoverBackgroundOnly
              hoverChromeOnly
              compactIconOnly={compact && !mediumIconOnly}
              mediumIconOnly={mediumIconOnly}
              size={mediumIconOnly ? 'medium' : 'small'}
              buttonProps={{...getTriggerProps(), 'aria-label': label, title: label}}
            />
          ) : (
            <button
              type="button"
              disabled={disabled}
              className={`${statusTagClassName} ${metricStatusStyles.metricStatusButton} ${statusControlStyles.interactiveStatusTrigger}`}
              {...getTriggerProps()}
              aria-label={label}
              title={label}
            >
              <OutcomeStatusTagContent status={selectedStatus} size={size} />
            </button>
          )}
          <OutcomeStatusMenuContent
            getContentProps={getContentProps}
            selectedStatus={selectedStatus}
            onSelect={(nextStatus) => onChange(getOutcomeStatusUpdate(nextStatus))}
          />
        </>
      )}
    </Menu.Wrapper>
  );
};
